// useIntlAccaHome — tournament + active slips (legs joined) + the
// current user's existing pass. Binds IntlAccaHomePage's DATA BINDING
// CONTRACT exactly. DOCTRINE: never fabricate — on error/empty every
// field is null/[] and the page renders its honest-empty state.

import { useCallback, useMemo } from "react";
import { useComposedQuery } from "../data/useTableQuery.ts";
import { useAuthSession } from "../auth/useAuthSession.ts";
import type {
  IntlAccaPassPurchase,
  IntlAccaSlip,
  IntlAccaSlipLeg,
  IntlAccaSlipWithLegs,
  IntlAccaTournament,
} from "../db/intl-acca-types.ts";

function mergeLegs(
  slips: IntlAccaSlip[],
  legs: IntlAccaSlipLeg[],
): IntlAccaSlipWithLegs[] {
  const bySlip = new Map<string, IntlAccaSlipLeg[]>();
  for (const l of legs) {
    if (!bySlip.has(l.slip_id)) bySlip.set(l.slip_id, []);
    bySlip.get(l.slip_id)!.push(l);
  }
  const rank: Record<string, number> = { FOCUS: 0, VALUE: 1, POWER: 2 };
  return slips
    .map((s) => ({ ...s, legs: bySlip.get(s.id) ?? [] }))
    .sort((a, b) => (rank[a.slip_type] ?? 9) - (rank[b.slip_type] ?? 9));
}

// PRD §4 Stream 2: the Intl Acca free trial is 3 days. We REUSE the
// platform trial clock (profiles.trial_start_at / tier_state) and just
// apply a product-specific 3-day ceiling here — no schema/edge change,
// no impact on the platform-wide 7-day trial for other products.
export const INTL_TRIAL_DAYS = 3;

/**
 * subscriber — real PAID tier ≥ sika_pro (via user_active_tier(), which
 *   is null for trial-only users). Sees the terminal feed (Direction A).
 * trial      — on the platform trial, still inside the 3-day Intl
 *   ceiling. Focus Acca unlocked; Value/Power Pass-only (PRD Stream 2).
 * teaser     — free / anon, trial not yet used. Desire teaser (§3.2).
 * expired    — trial used and past day 3 (or churned) and not a paid
 *   sub. HARD paywall, no grace (PRD Stream 2).
 */
export type IntlTrialPhase = "subscriber" | "trial" | "teaser" | "expired";

export interface IntlAccaHomeVM {
  loading: boolean;
  error: string | null;
  tournament: IntlAccaTournament | null;
  slips: IntlAccaSlipWithLegs[];
  /** The FOCUS slip — the only one the editorial (Direction B) shows. */
  focus: IntlAccaSlipWithLegs | null;
  existingPass: IntlAccaPassPurchase | null;
  /** Real paid tier from user_active_tier() — null for trial/free/anon. */
  subscriberTier: string | null;
  /** Trial funnel phase (PRD §4 Stream 2). */
  trialPhase: IntlTrialPhase;
  /** Trial day index 1..3 — only set when trialPhase === 'trial'. */
  trialDay: number | null;
  /** The viewer's referral code — embedded in shared slip cards (§12). */
  referralCode: string | null;
  matchLabels?: Record<string, string>;
  refetch: () => Promise<void>;
}

/** The composite the sequential pipeline resolves to (loading/error/refetch
 *  are owned by the query wrapper, not this payload). */
type IntlAccaHomeData = Omit<IntlAccaHomeVM, "loading" | "error" | "refetch">;

const EMPTY_HOME_DATA: IntlAccaHomeData = {
  tournament: null, slips: [], focus: null, existingPass: null,
  subscriberTier: null, trialPhase: "teaser", trialDay: null,
  referralCode: null,
};

export function useIntlAccaHome(tournamentId: string): IntlAccaHomeVM {
  const { user } = useAuthSession();
  const userId = user?.id ?? null;

  const q = useComposedQuery<IntlAccaHomeData>(
    ["intl-acca-home", tournamentId, userId],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (client) => {
      const supabase = client as any;
      if (!tournamentId) return EMPTY_HOME_DATA;

      const [tRes, slRes] = await Promise.all([
        supabase.from("intl_acca_tournaments").select("*").eq("id", tournamentId).maybeSingle(),
        supabase.from("intl_acca_slips").select("*")
          .eq("tournament_id", tournamentId).eq("status", "ACTIVE")
          .order("generated_at", { ascending: false }),
      ]);
      if (tRes.error) throw tRes.error;
      const slips = (slRes.data ?? []) as IntlAccaSlip[];
      const slipIds = slips.map((x) => x.id);
      const [lgRes, pRes] = await Promise.all([
        slipIds.length
          ? supabase.from("intl_acca_slip_legs").select("*").in("slip_id", slipIds)
          : Promise.resolve({ data: [], error: null }),
        user
          ? supabase.from("intl_acca_pass_purchases").select("*")
              .eq("user_id", user.id).eq("tournament_id", tournamentId).maybeSingle()
          : Promise.resolve({ data: null, error: null }),
      ]);
      const withLegs = mergeLegs(slips, (lgRes.data ?? []) as IntlAccaSlipLeg[]);

      // Resolve the trial funnel phase (PRD §4 Stream 2). Real paid
      // tier comes from user_active_tier() (null for trial/free), so a
      // trial user is NOT mistaken for a Sika Pro subscriber. The
      // platform trial clock (profiles.trial_start_at) is reused with a
      // 3-day Intl-Acca ceiling — no platform trial change.
      let subscriberTier: string | null = null;
      let trialPhase: IntlTrialPhase = "teaser";
      let trialDay: number | null = null;
      let referralCode: string | null = null;
      if (user) {
        const [tierRes, profRes, refRes] = await Promise.all([
          supabase.rpc("user_active_tier"),
          supabase.from("profiles")
            .select("tier_state,trial_start_at")
            .eq("id", user.id).maybeSingle(),
          // §12: pre-resolve the viewer's referral code so the share
          // card carries it synchronously at click time (no popup
          // blocker). Best-effort — generic landing if it fails.
          supabase.rpc("get_or_create_user_referral_code", {
            p_user_id: user.id,
          }),
        ]);
        referralCode =
          !refRes.error && typeof refRes.data === "string" && refRes.data
            ? (refRes.data as string)
            : null;
        subscriberTier =
          !tierRes.error && typeof tierRes.data === "string" && tierRes.data
            ? (tierRes.data as string)
            : null;
        const prof = (profRes.data ?? null) as
          | { tier_state: string | null; trial_start_at: string | null }
          | null;
        if (subscriberTier) {
          trialPhase = "subscriber";
        } else if (prof?.tier_state === "trial" && prof.trial_start_at) {
          const startMs = new Date(prof.trial_start_at).getTime();
          const day = Math.floor((Date.now() - startMs) / 86_400_000) + 1;
          if (day >= 1 && day <= INTL_TRIAL_DAYS) {
            trialPhase = "trial";
            trialDay = day;
          } else {
            trialPhase = "expired"; // past the 3-day ceiling, no grace
          }
        } else if (
          prof?.tier_state === "churned" ||
          prof?.tier_state === "free"
        ) {
          // Free with a prior trial_start_at ⇒ trial spent ⇒ expired;
          // otherwise a genuine free viewer ⇒ desire teaser.
          trialPhase = prof.trial_start_at ? "expired" : "teaser";
        } else {
          trialPhase = "teaser";
        }
      }

      return {
        tournament: (tRes.data as IntlAccaTournament | null) ?? null,
        slips: withLegs,
        focus: withLegs.find((x) => x.slip_type === "FOCUS") ?? null,
        existingPass: (pRes.data as IntlAccaPassPurchase | null) ?? null,
        subscriberTier, trialPhase, trialDay, referralCode,
      };
    },
  );

  const refetch = useCallback(async () => {
    await q.refetch();
  }, [q]);

  const data = q.data ?? EMPTY_HOME_DATA;
  return useMemo<IntlAccaHomeVM>(
    () => ({
      ...data,
      loading: q.isLoading,
      error: q.error ? q.error.message : null,
      refetch,
    }),
    [data, q.isLoading, q.error, refetch],
  );
}
